import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';

const Simulator = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  // Simulator status
  const [status, setStatus] = useState(null);
  const [stations, setStations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add stations form
  const [addForm, setAddForm] = useState({
    template: '',
    count: 1
  });
  const [addLoading, setAddLoading] = useState(false);

  // Per-station inline form state
  const [txForms, setTxForms] = useState({}); // { [hashId]: { showStart, showStop, connectorId, idTag, transactionId } }
  const [actionLoading, setActionLoading] = useState({}); // { [hashId_action]: true }

  const isAdmin = user?.role === 'admin';

  const api = apiClient;

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/simulator/status');
      setStatus(response.data);
    } catch (err) {
      setStatus({ status: 'disconnected' });
    }
  }, [api]);

  const fetchStations = useCallback(async () => {
    try {
      const { data } = await api.get('/simulator/stations');
      setStations(data?.chargingStations || data?.stations || (Array.isArray(data) ? data : []));
    } catch (err) {
      // Silently handle - status will show disconnected
    }
  }, [api]);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/simulator/templates');
      const tmpl = data?.templates || (Array.isArray(data) ? data : []);
      setTemplates(tmpl);
      if (tmpl.length > 0) {
        setAddForm(prev => {
          if (prev.template) return prev;
          return { ...prev, template: tmpl[0].id || tmpl[0].name || tmpl[0] };
        });
      }
    } catch (err) {
      // Silently handle
    }
  }, [api]);

  // Initial load
  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }

    const loadInitial = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchStations(), fetchTemplates()]);
      setLoading(false);
    };

    loadInitial();
  }, [isAdmin, fetchStatus, fetchStations, fetchTemplates]);

  // Polling every 5 seconds
  useEffect(() => {
    if (!isAdmin) return;

    const interval = setInterval(() => {
      fetchStatus();
      fetchStations();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAdmin, fetchStatus, fetchStations]);

  // Helper to set loading for a specific action
  const setActionLoad = (hashId, action, value) => {
    setActionLoading(prev => ({ ...prev, [`${hashId}_${action}`]: value }));
  };

  const isActionLoading = (hashId, action) => {
    return !!actionLoading[`${hashId}_${action}`];
  };

  // Get or initialize per-station form state
  const getTxForm = (hashId) => {
    return txForms[hashId] || {
      showStart: false,
      showStop: false,
      connectorId: 1,
      idTag: 'USER_ID_123',
      transactionId: ''
    };
  };

  const updateTxForm = (hashId, updates) => {
    setTxForms(prev => ({
      ...prev,
      [hashId]: { ...getTxForm(hashId), ...updates }
    }));
  };

  // --- Actions ---

  const handleAddStations = async (e) => {
    e.preventDefault();
    if (!addForm.template) {
      addToast('Please select a template', 'error');
      return;
    }
    setAddLoading(true);
    try {
      await api.post('/simulator/stations', {
        template: addForm.template,
        count: parseInt(addForm.count)
      });
      addToast(`Added ${addForm.count} station(s) successfully`, 'success');
      await fetchStations();
    } catch (err) {
      addToast('Failed to add stations: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleStartStation = async (hashId) => {
    setActionLoad(hashId, 'start', true);
    try {
      await api.post(`/simulator/stations/${hashId}/start`);
      addToast('Station started', 'success');
      await fetchStations();
    } catch (err) {
      addToast('Failed to start station: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoad(hashId, 'start', false);
    }
  };

  const handleStopStation = async (hashId) => {
    setActionLoad(hashId, 'stop', true);
    try {
      await api.post(`/simulator/stations/${hashId}/stop`);
      addToast('Station stopped', 'success');
      await fetchStations();
    } catch (err) {
      addToast('Failed to stop station: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoad(hashId, 'stop', false);
    }
  };

  const handleStartTransaction = async (hashId) => {
    const form = getTxForm(hashId);
    setActionLoad(hashId, 'startTx', true);
    try {
      await api.post(`/simulator/stations/${hashId}/start-transaction`, {
        connector_id: parseInt(form.connectorId),
        id_tag: form.idTag
      });
      addToast('Transaction started', 'success');
      updateTxForm(hashId, { showStart: false });
      await fetchStations();
    } catch (err) {
      addToast('Failed to start transaction: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoad(hashId, 'startTx', false);
    }
  };

  const handleStopTransaction = async (hashId) => {
    const form = getTxForm(hashId);
    if (!form.transactionId) {
      addToast('Please enter a transaction ID', 'error');
      return;
    }
    setActionLoad(hashId, 'stopTx', true);
    try {
      await api.post(`/simulator/stations/${hashId}/stop-transaction`, {
        transaction_id: parseInt(form.transactionId)
      });
      addToast('Transaction stopped', 'success');
      updateTxForm(hashId, { showStop: false, transactionId: '' });
      await fetchStations();
    } catch (err) {
      addToast('Failed to stop transaction: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoad(hashId, 'stopTx', false);
    }
  };

  const handleAutoTransactions = async (hashId, action) => {
    setActionLoad(hashId, 'auto', true);
    try {
      await api.post(`/simulator/stations/${hashId}/auto-transactions`, { action });
      addToast(`Auto transactions ${action === 'start' ? 'started' : 'stopped'}`, 'success');
      await fetchStations();
    } catch (err) {
      addToast('Failed to toggle auto transactions: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoad(hashId, 'auto', false);
    }
  };

  const handleDeleteStation = async (hashId) => {
    if (!window.confirm(`Are you sure you want to delete station ${hashId}?`)) {
      return;
    }
    setActionLoad(hashId, 'delete', true);
    try {
      await api.delete('/simulator/stations', { data: { hash_ids: [hashId] } });
      addToast('Station deleted', 'success');
      await fetchStations();
    } catch (err) {
      addToast('Failed to delete station: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoad(hashId, 'delete', false);
    }
  };

  // --- Styles ---

  const styles = {
    container: {
      padding: '1rem',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    banner: {
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '6px',
      padding: '0.75rem 1rem',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    bannerText: {
      fontWeight: '600',
      color: '#856404',
      fontSize: '1rem'
    },
    bannerBadge: {
      backgroundColor: '#ffc107',
      color: '#856404',
      padding: '0.15rem 0.5rem',
      borderRadius: '3px',
      fontSize: '0.75rem',
      fontWeight: '700',
      textTransform: 'uppercase'
    },
    card: {
      backgroundColor: '#fff',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '1.25rem',
      marginBottom: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    },
    cardTitle: {
      margin: '0 0 1rem 0',
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#333'
    },
    statusRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap'
    },
    statusDot: (connected) => ({
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: connected ? '#28a745' : '#dc3545',
      display: 'inline-block',
      flexShrink: 0
    }),
    statusLabel: (connected) => ({
      fontWeight: '600',
      color: connected ? '#28a745' : '#dc3545',
      fontSize: '0.95rem'
    }),
    statusDetail: {
      fontSize: '0.85rem',
      color: '#6c757d'
    },
    formRow: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '1rem',
      flexWrap: 'wrap'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.35rem'
    },
    label: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#495057'
    },
    select: {
      padding: '0.5rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.9rem',
      minWidth: '200px',
      backgroundColor: '#fff'
    },
    inputSmall: {
      padding: '0.5rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.9rem',
      width: '80px'
    },
    input: {
      padding: '0.5rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.9rem',
      width: '140px'
    },
    btnPrimary: {
      padding: '0.5rem 1rem',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: '500'
    },
    btnSuccess: {
      padding: '0.35rem 0.7rem',
      backgroundColor: '#28a745',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontWeight: '500'
    },
    btnDanger: {
      padding: '0.35rem 0.7rem',
      backgroundColor: '#dc3545',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontWeight: '500'
    },
    btnWarning: {
      padding: '0.35rem 0.7rem',
      backgroundColor: '#ffc107',
      color: '#212529',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontWeight: '500'
    },
    btnSecondary: {
      padding: '0.35rem 0.7rem',
      backgroundColor: '#6c757d',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontWeight: '500'
    },
    btnOutline: {
      padding: '0.35rem 0.7rem',
      backgroundColor: 'transparent',
      color: '#6c757d',
      border: '1px solid #6c757d',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontWeight: '500'
    },
    btnDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '0.6rem 0.75rem',
      borderBottom: '2px solid #dee2e6',
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#495057',
      backgroundColor: '#f8f9fa'
    },
    td: {
      padding: '0.6rem 0.75rem',
      borderBottom: '1px solid #e9ecef',
      fontSize: '0.85rem',
      verticalAlign: 'top'
    },
    badge: (color) => ({
      display: 'inline-block',
      padding: '0.2rem 0.55rem',
      borderRadius: '3px',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#fff',
      backgroundColor: color
    }),
    inlineForm: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      border: '1px solid #e9ecef',
      flexWrap: 'wrap'
    },
    inlineInput: {
      padding: '0.3rem 0.5rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.8rem',
      width: '110px'
    },
    actionsCell: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.35rem'
    },
    actionRow: {
      display: 'flex',
      gap: '0.35rem',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6c757d',
      fontSize: '0.95rem'
    },
    spinner: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '3rem',
      fontSize: '1rem',
      color: '#6c757d'
    },
    accessDenied: {
      textAlign: 'center',
      padding: '3rem',
      color: '#dc3545'
    }
  };

  // --- Render ---

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need admin privileges to access the simulator controls.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.banner}>
          <span style={styles.bannerBadge}>DEV</span>
          <span style={styles.bannerText}>SAP e-Mobility Simulator - Development Tool</span>
        </div>
        <div style={styles.spinner}>Loading simulator data...</div>
      </div>
    );
  }

  const isConnected = status && status.status !== 'disconnected' && status.status !== 'error';
  const simVersion = status?.state?.state?.version || status?.version;

  const getTemplateLabel = (tmpl) => {
    if (typeof tmpl === 'string') return tmpl;
    return tmpl.name || tmpl.id || JSON.stringify(tmpl);
  };

  const getTemplateValue = (tmpl) => {
    if (typeof tmpl === 'string') return tmpl;
    return tmpl.id || tmpl.name || JSON.stringify(tmpl);
  };

  const truncateHash = (hashId) => {
    if (!hashId) return '';
    if (hashId.length <= 12) return hashId;
    return hashId.substring(0, 10) + '...';
  };

  // SAP simulator nests station info: stationInfo.hashId, stationInfo.chargingStationId, etc.
  const getHashId = (station) => {
    return station.stationInfo?.hashId || station.hash_id || station.hashId || station.id || '';
  };

  const getStationName = (station) => {
    return station.stationInfo?.chargingStationId || station.name || station.station_name || station.identity || getHashId(station);
  };

  const getStationStatus = (station) => {
    // SAP simulator uses boolean `started` field
    if (typeof station.started === 'boolean') {
      return station.started ? 'started' : 'stopped';
    }
    const s = station.status || station.state || 'unknown';
    return s.toLowerCase();
  };

  const getStatusBadge = (station) => {
    const s = getStationStatus(station);
    if (s === 'started' || s === 'running' || s === 'connected' || s === 'available') {
      return { label: 'Started', color: '#28a745' };
    }
    if (s === 'stopped' || s === 'disconnected' || s === 'unavailable') {
      return { label: 'Stopped', color: '#dc3545' };
    }
    return { label: s.charAt(0).toUpperCase() + s.slice(1), color: '#6c757d' };
  };

  const getConnectorsDisplay = (station) => {
    const connectors = station.connectors || station.connector_count || station.evses;
    if (Array.isArray(connectors)) {
      // SAP simulator includes connector 0 (station-level), real connectors start at index 1
      return Math.max(connectors.length - 1, 0);
    }
    if (typeof connectors === 'number') return connectors;
    return '-';
  };

  return (
    <div style={styles.container}>
      {/* Yellow Dev-Tool Banner */}
      <div style={styles.banner}>
        <span style={styles.bannerBadge}>DEV</span>
        <span style={styles.bannerText}>SAP e-Mobility Simulator - Development Tool</span>
      </div>

      {/* Status Section */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Simulator Status</h3>
        <div style={styles.statusRow}>
          <span style={styles.statusDot(isConnected)} />
          <span style={styles.statusLabel(isConnected)}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {simVersion && (
            <span style={styles.statusDetail}>Version: {simVersion}</span>
          )}
          {status?.url && (
            <span style={styles.statusDetail}>URL: {status.url}</span>
          )}
          {status?.station_count !== undefined && (
            <span style={styles.statusDetail}>Stations: {status.station_count}</span>
          )}
        </div>
      </div>

      {/* Add Stations Form */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Add Stations</h3>
        <form onSubmit={handleAddStations}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Template</label>
              <select
                value={addForm.template}
                onChange={(e) => setAddForm({ ...addForm, template: e.target.value })}
                style={styles.select}
                disabled={templates.length === 0}
              >
                {templates.length === 0 && (
                  <option value="">No templates available</option>
                )}
                {templates.map((tmpl, i) => (
                  <option key={i} value={getTemplateValue(tmpl)}>
                    {getTemplateLabel(tmpl)}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Count</label>
              <input
                type="number"
                min="1"
                max="50"
                value={addForm.count}
                onChange={(e) => setAddForm({ ...addForm, count: parseInt(e.target.value) || 1 })}
                style={styles.inputSmall}
              />
            </div>
            <button
              type="submit"
              style={{
                ...styles.btnPrimary,
                ...(addLoading || templates.length === 0 ? styles.btnDisabled : {})
              }}
              disabled={addLoading || templates.length === 0}
            >
              {addLoading ? 'Adding...' : 'Add Stations'}
            </button>
          </div>
        </form>
      </div>

      {/* Stations Table */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Stations ({stations.length})</h3>

        {error && (
          <div style={{ color: '#dc3545', marginBottom: '1rem', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        {stations.length === 0 ? (
          <div style={styles.emptyState}>
            No stations configured. Use the form above to add stations from a template.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Hash ID</th>
                  <th style={styles.th}>Station Name</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Connectors</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stations.map((station) => {
                  const hashId = getHashId(station);
                  const stationName = getStationName(station);
                  const stationStatus = getStationStatus(station);
                  const statusBadge = getStatusBadge(station);
                  const form = getTxForm(hashId);
                  const isStopped = stationStatus === 'stopped' || stationStatus === 'disconnected';
                  const isStarted = !isStopped;

                  return (
                    <tr key={hashId}>
                      <td style={styles.td} title={hashId}>
                        <code style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa', padding: '0.15rem 0.35rem', borderRadius: '3px' }}>
                          {truncateHash(hashId)}
                        </code>
                      </td>
                      <td style={styles.td}>
                        <strong>{stationName}</strong>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.badge(statusBadge.color)}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {getConnectorsDisplay(station)}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionsCell}>
                          {/* Start / Stop station */}
                          <div style={styles.actionRow}>
                            {isStopped ? (
                              <button
                                style={{
                                  ...styles.btnSuccess,
                                  ...(isActionLoading(hashId, 'start') ? styles.btnDisabled : {})
                                }}
                                disabled={isActionLoading(hashId, 'start')}
                                onClick={() => handleStartStation(hashId)}
                              >
                                {isActionLoading(hashId, 'start') ? 'Starting...' : 'Start'}
                              </button>
                            ) : (
                              <button
                                style={{
                                  ...styles.btnWarning,
                                  ...(isActionLoading(hashId, 'stop') ? styles.btnDisabled : {})
                                }}
                                disabled={isActionLoading(hashId, 'stop')}
                                onClick={() => handleStopStation(hashId)}
                              >
                                {isActionLoading(hashId, 'stop') ? 'Stopping...' : 'Stop'}
                              </button>
                            )}

                            {/* Start Transaction toggle */}
                            <button
                              style={isStarted ? styles.btnOutline : { ...styles.btnOutline, ...styles.btnDisabled }}
                              disabled={!isStarted}
                              onClick={() => updateTxForm(hashId, { showStart: !form.showStart, showStop: false })}
                            >
                              {form.showStart ? 'Cancel' : 'Start Tx'}
                            </button>

                            {/* Stop Transaction toggle */}
                            <button
                              style={isStarted ? styles.btnOutline : { ...styles.btnOutline, ...styles.btnDisabled }}
                              disabled={!isStarted}
                              onClick={() => updateTxForm(hashId, { showStop: !form.showStop, showStart: false })}
                            >
                              {form.showStop ? 'Cancel' : 'Stop Tx'}
                            </button>

                            {/* Auto Transaction toggle */}
                            {station.auto_transactions_active || station.autoTransactions ? (
                              <button
                                style={{
                                  ...styles.btnDanger,
                                  fontSize: '0.75rem',
                                  ...(isActionLoading(hashId, 'auto') ? styles.btnDisabled : {})
                                }}
                                disabled={isActionLoading(hashId, 'auto')}
                                onClick={() => handleAutoTransactions(hashId, 'stop')}
                              >
                                {isActionLoading(hashId, 'auto') ? '...' : 'Auto Tx OFF'}
                              </button>
                            ) : (
                              <button
                                style={{
                                  ...styles.btnSuccess,
                                  fontSize: '0.75rem',
                                  ...(isActionLoading(hashId, 'auto') || !isStarted ? styles.btnDisabled : {})
                                }}
                                disabled={isActionLoading(hashId, 'auto') || !isStarted}
                                onClick={() => handleAutoTransactions(hashId, 'start')}
                              >
                                {isActionLoading(hashId, 'auto') ? '...' : 'Auto Tx ON'}
                              </button>
                            )}

                            {/* Delete */}
                            <button
                              style={{
                                ...styles.btnDanger,
                                ...(isActionLoading(hashId, 'delete') ? styles.btnDisabled : {})
                              }}
                              disabled={isActionLoading(hashId, 'delete')}
                              onClick={() => handleDeleteStation(hashId)}
                            >
                              {isActionLoading(hashId, 'delete') ? '...' : 'Delete'}
                            </button>
                          </div>

                          {/* Inline Start Transaction form */}
                          {form.showStart && (
                            <div style={styles.inlineForm}>
                              <label style={{ fontSize: '0.8rem', color: '#495057', fontWeight: '500' }}>Connector:</label>
                              <input
                                type="number"
                                min="1"
                                value={form.connectorId}
                                onChange={(e) => updateTxForm(hashId, { connectorId: parseInt(e.target.value) || 1 })}
                                style={{ ...styles.inlineInput, width: '60px' }}
                              />
                              <label style={{ fontSize: '0.8rem', color: '#495057', fontWeight: '500' }}>ID Tag:</label>
                              <input
                                type="text"
                                value={form.idTag}
                                onChange={(e) => updateTxForm(hashId, { idTag: e.target.value })}
                                style={styles.inlineInput}
                              />
                              <button
                                style={{
                                  ...styles.btnSuccess,
                                  ...(isActionLoading(hashId, 'startTx') ? styles.btnDisabled : {})
                                }}
                                disabled={isActionLoading(hashId, 'startTx')}
                                onClick={() => handleStartTransaction(hashId)}
                              >
                                {isActionLoading(hashId, 'startTx') ? 'Starting...' : 'Go'}
                              </button>
                            </div>
                          )}

                          {/* Inline Stop Transaction form */}
                          {form.showStop && (
                            <div style={styles.inlineForm}>
                              <label style={{ fontSize: '0.8rem', color: '#495057', fontWeight: '500' }}>Transaction ID:</label>
                              <input
                                type="number"
                                min="1"
                                value={form.transactionId}
                                onChange={(e) => updateTxForm(hashId, { transactionId: e.target.value })}
                                style={{ ...styles.inlineInput, width: '90px' }}
                                placeholder="e.g. 123"
                              />
                              <button
                                style={{
                                  ...styles.btnDanger,
                                  ...(isActionLoading(hashId, 'stopTx') ? styles.btnDisabled : {})
                                }}
                                disabled={isActionLoading(hashId, 'stopTx')}
                                onClick={() => handleStopTransaction(hashId)}
                              >
                                {isActionLoading(hashId, 'stopTx') ? 'Stopping...' : 'Stop'}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Simulator;
